import { useCallback, useEffect, useRef, useState } from 'react';
import videojs, { VideoJsPlayer } from 'video.js';
import Vast from './Plugins/Vast';
import 'videojs-contrib-quality-levels';
import { Sources } from './PlayerModels';
import { AUTOPLAY_SUCCESS, CONTENT_END_EVENTS, handleRangeQualities, timeToSeconds } from './PlayerServices';
import options from './PlayerConfigs';
import PlayerCustomComponents from './components/PlayerCustomComponents';
import PlayerVastComponents from './components/PlayerVastComponents';
import UnmuteNotice from './components/UnmuteNotice';
import ErrorModal from './components/ErrorModal';
import UsePlayerProps from '../../Store/usePlayerProps';

export default function VideoPlayer(props: {
  toggleLoader?: () => void;
  isShort?: boolean;
  onShowInfo?: () => void;
  onEnded?: () => void;
}) {
  const setPlayerGlobalState = UsePlayerProps((state) => state.setPlayerState);
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const IS_IOS = /iPad|iPhone|iPod|Macintosh|macintosh/.test(navigator.userAgent);
  const { toggleLoader, isShort = false, onShowInfo, onEnded } = props;
  const { contentConfigs, contentData, sources } = playerGlobalState;
  const { videos, captions } = sources as Sources;
  const isLive = contentConfigs?.playback_type === 'live_stream';
  // const { tech } = useTech();
  const videoRef = useRef<any>(null);
  const playerRef = useRef<VideoJsPlayer>(null);
  const [playerState, setPlayerState] = useState<VideoJsPlayer>();
  const [playingAd, setPlayingAd] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [vastError, setVastError] = useState<boolean>(false);
  const [hasNoPreroll, setHasNoPreroll] = useState<boolean>(false);

  // const [decodeErr, setDecodeErr] = useState<boolean>(false);
  const initialPlay = useRef<boolean>(false);
  const handleKeyDown = useCallback((e: KeyboardEvent) => handleKeyDownAction(e, playerRef.current), []);

  useEffect(() => {
    if (playerRef.current) return;
    // videojs.Vhs.GOAL_BUFFER_LENGTH = 60;
    // videojs.Vhs.MAX_GOAL_BUFFER_LENGTH = 60;
    const videoElement = document.createElement('video');
    IS_IOS && videoElement.setAttribute('playsinline', 'playsinline');
    videoElement.classList.add('custom-player');
    IS_IOS && videoElement.classList.add('native-custom-player');
    videoRef?.current?.appendChild(videoElement);

    if (!videoElement) return;

    const playerOptions = {
      ...options,
      html5: {
        ...options.html5,
        ...(isLive && { nativeTextTracks: false }),
      },
    };

    if (videoElement && videos.length) {
      playerRef.current = videojs(
        videoElement,
        {
          ...playerOptions,
          // sources: tech !== 'mp4' ? videos : playableSource || videos.reverse(),
          tracks: captions,
          poster: contentData?.cover,
        },
        () => {
          toggleLoader?.();
        },
      );

      // The muted retry of `autoplay: 'any'` (PlayerConfigs) must cover the very first automatic
      // start only. video.js runs it on every loadstart and reads any rejected play() as a
      // blocked autoplay, so a play() interrupted by the next source - a preroll replacing the
      // content, the content coming back when the ad break ends - would mute the player behind
      // the viewer's back. Once something is playing, the following sources only need a plain
      // play(). On success only: a failure means nothing started, and downgrading there left
      // the preroll with a plain unmuted play() the browser refused again, so playback never
      // began. Attached synchronously: the attempt can settle before the UI components mount.
      playerRef.current.one(AUTOPLAY_SUCCESS, () => {
        if (!playerRef.current) return;
        // Whether playback was only allowed because it was muted. Anything that unmutes on its
        // own (the live-stream reset in the Vast plugin) has to leave that alone.
        playerRef.current.cache_.autoplayNeededMuted = playerRef.current.muted();
        playerRef.current.autoplay('play');
      });

      // Set here rather than through the `sources` option. Given in the options, the first source
      // has to load the tech, and video.js clears its "source is changing" flag on a timer after
      // that. Whenever the video's `loadstart` gets in before that timer - timers lag behind on a
      // busy or throttled page - the `autoplay: 'any'` attempt is deferred to a `ready` or
      // `loadstart` that has already fired, and lost: no play(), no muted retry, the player sits
      // on its poster until a tap. Created without sources, the player loads its tech right away,
      // so the source is set and the flag cleared in one step.
      playerRef.current.src(videos);
    }
    setPlayerState(playerRef.current);

    return () => {
      setPlayerGlobalState({
        contentConfigs: null,
        contentData: null,
        sources: null,
        vttData: null,
        captionState: {
          prevCaptionIndex: -1,
          selectedCaptionLang: {
            lang: 'off',
            label: 'خاموش',
          },
        },
        showingNextEpisode: true,
      });
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      document && document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (playerState) {
      // Register Vast Plugin
      if (contentConfigs?.vast_url) {
        register();
        new Vast(playerState, {
          vmapUrl: contentConfigs?.vast_url,
          addCtaClickZone: false,
          timeout: 7000,
          verificationTimeout: 5000,
          isLive,
          isIos: IS_IOS,
        });
      } else {
        setHasNoPreroll(true);
        document && document.addEventListener('keydown', handleKeyDown);
      }

      // Vast Plugin Events
      // playerState.on('adplaying', () => {
      //   if (isLive) {
      //     setTimeout(() => {
      //       playerState.muted(false);
      //     }, 200);
      //   }
      // });

      // 'adstart' fires once per ad break, 'vast.play' once per ad in it. The content settings
      // have to be snapshotted on the break, before the first ad replaces the source: taken
      // again between two ads of the same break, it would snapshot the ad's own empty audio,
      // caption and quality lists and handleAdComplete would have nothing left to restore.
      const handleAdStart = () => {
        handleAdStarted();
      };
      const handleVastPlay = () => {
        setPlayingAd(true);
      };
      const handleVastComplete = () => {
        setPlayingAd(false);
        setHasNoPreroll(true);
        setVastError(false);
        handleAdComplete();
        document && document.addEventListener('keydown', handleKeyDown);
      };
      const handleVastError = () => {
        setVastError(true);
        setPlayingAd(false);
        document && document.addEventListener('keydown', handleKeyDown);
      };
      const handleNoPreroll = () => {
        setHasNoPreroll(true);
      };

      playerState.on('adstart', handleAdStart);
      playerState.on('vast.play', handleVastPlay);
      playerState.on('vast.complete', handleVastComplete);
      playerState.on('vast.error', handleVastError);
      playerState.on('nopreroll', handleNoPreroll);

      // Removed by reference: a bare playerState.off('vast.complete') would also drop the
      // listeners PlayerCustomComponents registers on this same player.
      return () => {
        playerState.off('adstart', handleAdStart);
        playerState.off('vast.play', handleVastPlay);
        playerState.off('vast.complete', handleVastComplete);
        playerState.off('vast.error', handleVastError);
        playerState.off('nopreroll', handleNoPreroll);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerState]);

  // Not bound while an ad is playing: the ad shares this player, so its own `ended` would
  // otherwise be read as the content finishing.
  useEffect(() => {
    if (!playerState || !onEnded || playingAd) return;

    // An ad break makes contrib-ads announce the end twice (see CONTENT_END_EVENTS); advancing
    // on both would skip an episode.
    let advanced = false;
    const handleEnded = () => {
      if (advanced) return;
      advanced = true;
      onEnded();
    };

    playerState.on(CONTENT_END_EVENTS, handleEnded);
    return () => playerState.off(CONTENT_END_EVENTS, handleEnded);
  }, [playerState, onEnded, playingAd]);

  useEffect(() => {
    // Handle resume time
    if (!playerState) return;

    const handleResume = () => {
      if (!playingAd && !isLive && !initialPlay.current) {
        playerState.currentTime(timeToSeconds(contentConfigs?.recommended_start));
        initialPlay.current = true;
      }
    };

    playerState.one('playing', handleResume);
    // This effect re-runs on every playingAd change, so the one-shot left by the previous run
    // has to go or they pile up on the player.
    return () => playerState.off('playing', handleResume);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerState, playingAd]);

  const handleAdStarted = () => {
    playerState.cache_.selectedAudioTrack = playerState.audioTracks().tracks_.find((track) => track.enabled)?.language || '';

    playerState.cache_.selectedPlaybackRate = playerState.playbackRate();

    playerState.cache_.selectedTextTrack = playerState.textTracks().tracks_.find((track) => track.mode === 'showing')?.language || '';

    // Only a quality the viewer picked is restored. In Auto every level is enabled, and pinning
    // the first of them (or 240p, before any level has loaded) switched adaptive bitrate off.
    const levels = playerState.qualityLevels().levels_;
    const enabled = levels.filter((level) => level.enabled);
    playerState.cache_.selectedQuaity = enabled.length && enabled.length < levels.length
      ? handleRangeQualities(enabled[0].height)
      : '';
  };

  const handleAdComplete = () => {
    const handleRestoreSettings = () => {
      if (playerState.cache_) {
        // Set last playback rate
        playerState.playbackRate(playerState.cache_.selectedPlaybackRate);

        // Set last audio
        if (playerState.cache_.selectedAudioTrack) {
          const audioTracks = playerState.audioTracks().tracks_;
          audioTracks.length &&
            audioTracks.forEach((elem) => {
              elem.enabled = elem.language === playerState.cache_.selectedAudioTrack;
            });
        }

        // Set last caption
        if (playerState.cache_.selectedTextTrack) {
          const _player = playerState.textTracks().tracks_;
          const index = _player.findIndex((elem) => elem.language === playerState.cache_.selectedTextTrack);
          _player.forEach((elem, i) => {
            elem.mode = 'disabled';
            if (i === index) {
              elem.mode = 'showing';
            }
          });
        }

        // Set last quality
        if (playerState.cache_.selectedQuaity) {
          playerState.qualityLevels().levels_.length &&
            playerState.qualityLevels().levels_.forEach((elem) => {
              if (handleRangeQualities(Number(elem.height)) === Number(playerState.cache_.selectedQuaity)) {
                elem.enabled_(true);
              } else {
                elem.enabled_(false);
              }
            });
        }
        // No position to restore here, live or not: contrib-ads' snapshot already brings the
        // content back to where the ad interrupted it.
      }
    };

    playerState.one('playing', handleRestoreSettings);
  };

  const hasAdsPlugin = () => {
    if (videojs.getPlugin) {
      return Boolean(videojs.getPlugin('vast'));
    }
  };

  const register = () => {
    if (hasAdsPlugin()) {
      return;
    }
    const registerPlugin = videojs.registerPlugin || videojs.plugin;
    registerPlugin('vast', Vast);
  };

  const handleKeyDownAction = (e, player) => {
    if (player) {
      const playerVolume = player.volume();
      const playerCurrentTime = player.currentTime();
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          if (player.paused()) {
            player.play();
          } else {
            player.pause();
          }
          break;
        case 'Enter':
          if (player.paused()) {
            player.play();
          } else {
            player.pause();
          }
          break;
        case 'ArrowRight':
          player.currentTime(playerCurrentTime + 15);
          break;
        case 'ArrowLeft':
          player.currentTime(playerCurrentTime - 15);
          break;
        case 'ArrowUp':
          // Turning the volume up on a muted player (a muted autoplay, for instance) has to
          // unmute it, otherwise the key does nothing audible.
          player.muted(false);
          if (playerVolume !== 1) {
            player.volume(playerVolume + 0.1);
          }
          break;
        case 'ArrowDown':
          if (playerVolume !== 0) {
            player.volume(playerVolume - 0.1);
          }
          break;
        case 'KeyM':
          player.muted(!player.muted());
          if (!player.volume()) {
            player.volume(1);
          }
          break;
        default:
          return;
      }
    }
  };

  if (showError && !vastError && hasNoPreroll) {
    return <ErrorModal player={playerState} isLive={isLive} isShort={isShort} />;
  }

  return (
    <div ref={videoRef} id="custom-player-wrapper" className={isShort ? 'short-player' : undefined}>
      {playerState && (
        <>
          <PlayerVastComponents player={playerState} playingAd={playingAd} isShort={isShort} />
          {videos.length && <PlayerCustomComponents
            playingAd={playingAd}
            player={playerState}
            isShort={isShort}
            onShowInfo={onShowInfo}
            handleErr={() => setShowError(true)}
          />}
          {/* Outside both, and last, so the notice is shown over an ad as well as over the
              content, and its click zone stays on top of the one that toggles playback. */}
          <UnmuteNotice player={playerState} />
        </>
      )}
    </div>
  );
}
